import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';

@Injectable()
export class HttpInterceptorInterceptor implements HttpInterceptor {
  constructor(
    private readonly router: Router,
    private readonly msalService: MsalService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const matchingConfig = this.getMatchingApiConfig(req.url);
    if (!matchingConfig) {
      return this.handleRequestWithoutToken(req, next);
    }

    const account = this.getCurrentAccount();
    const scope = matchingConfig.audienceId
      ? `${matchingConfig.audienceId}/.default`
      : '';
    if (!account || !scope) {
      return this.handleRequestWithoutToken(req, next);
    }

    const tokenRequest = { scopes: [scope], account: account };
    return from(this.msalService.acquireTokenSilent(tokenRequest)).pipe(
      switchMap((response) => {
        const authReq = req.clone({
          setHeaders: { Authorization: `Bearer ${response.accessToken}` },
        });
        return next.handle(authReq).pipe(
          catchError((httpError: HttpErrorResponse) => {
            return this.handleHttpError(httpError, req, next);
          })
        );
      }),
      catchError((error: any) =>
        this.handleTokenError(error, tokenRequest)
      )
    );
  }

  /**
   * Finds the first API config matching the request URL.
   */
  private getMatchingApiConfig(url: string): any {
    const apiConfigs = environment.graphToolkit?.apiConfigs || [];
    return (
      apiConfigs.find((config: any) =>
        new RegExp(config.urlPattern).test(url)
      ) || null
    );
  }

  /**
   * Returns the current MSAL account, preferring the active account.
   */
  private getCurrentAccount(): any {
    const accounts = this.msalService.instance.getAllAccounts();
    return (
      this.msalService.instance.getActiveAccount() ||
      (accounts?.length > 0 ? accounts[0] : null)
    );
  }

  /**
   * Handles requests that do not require a token.
   */
  private handleRequestWithoutToken(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next
      .handle(req)
      .pipe(
        catchError((error: HttpErrorResponse) => this.handleHttpError(error, req, next))
      );
  }

  /**
   * Handles errors during token acquisition or HTTP errors.
   */
  private handleTokenError(
    error: any,
    tokenRequest: any,
  ): Observable<HttpEvent<any>> {
    if (
      error?.name === 'InteractionRequiredAuthError' ||
      error?.errorCode === 'interaction_required'
    ) {
      this.msalService.acquireTokenRedirect(tokenRequest);
      return throwError(() => error);
    }
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401) {
        void this.router.navigate(['/login']);
      }
      return throwError(() => error);
    }
    return throwError(() => error);
  }

  /**
   * Handles HTTP errors, redirecting or logging as appropriate.
   */
  private handleHttpError(error: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (error.status === 401) {
      const account = this.getCurrentAccount();
      if (account) {
        const matchingConfig = this.getMatchingApiConfig(req.url);
        const scope = matchingConfig?.audienceId 
          ? `${matchingConfig.audienceId}/.default`
          : 'openid profile email';
        
        if (scope && scope !== 'openid profile email') {
          const tokenRequest = {
            scopes: [scope],
            account,
            forceRefresh: true
          };

          return from(this.msalService.instance.acquireTokenSilent(tokenRequest)).pipe(
            switchMap((result) => {
              const clonedRequest = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${result.accessToken}`
                }
              });
              return next.handle(clonedRequest);
            }),
            catchError((refreshError) => {
              void this.router.navigate(['/login']);
              return throwError(() => refreshError);
            })
          );
        }
      }
      void this.router.navigate(['/login']);
    } else if (error.status === 403) {
      console.error('Forbidden request');
    }
    return throwError(() => error);
  }
}
